import React, {useState} from 'react'
import {useNavigate, useParams, useLocation} from 'react-router-dom'
import {PageTitle} from '../../../_metronic/layout/core'
import {CreateServicioForm} from './components/CreateServicioForm'
import {Servicio} from './core/models'
import {fetchWithAuth} from '../../core/utils/fetchWithAuth'
import {KTIcon} from '../../../_metronic/helpers'

// Interfaz para el estado de navegación
interface LocationState {
  servicio?: Servicio
}

const EditServicioPage: React.FC = () => {
  const navigate = useNavigate()
  const {id} = useParams<{id: string}>()
  const location = useLocation()
  
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  
  // Obtener el servicio desde el state de navegación
  const servicio = (location.state as LocationState)?.servicio

  const getToken = (): string | null => {
    return localStorage.getItem('X-TOKEN')
  }

  const handleSubmit = async (servicioData: Omit<Servicio, 'id'>) => {
    try {
      setLoading(true)
      setError(null)
      
      const token = getToken()
      if (!token) {
        throw new Error('No se encontró el token de autenticación')
      }

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token
        },
        body: JSON.stringify({
          service: 'updateConcepto',
          id: parseInt(id!),
          nombre: servicioData.nombre,
          valor: servicioData.valor,
          descripcion: servicioData.descripcion,
          variable: servicioData.variable ? 1 : 0,
          recurrente: servicioData.recurrente ? 1 : 0,
          estado: servicioData.estado ? 1 : 0,
          exonerado: servicioData.exonerado ? 1 : 0,
          enviar_facturacion_electronica: servicioData.enviar_facturacion_electronica ? 1 : 0,
          codigo_contable: servicioData.codigo_contable || '',
        })
      })

      if (!response.ok) {
        throw new Error(`Error en la petición: ${response.status}`)
      }

      const data = await response.json()
      
      if (data.code === 'OK') {
        // Servicio actualizado exitosamente, redirigir a la lista
        navigate('/servicios')
      } else if (data.code === 'ERROR' && data.returned_message && data.returned_message.includes('JWT expired')) {
        localStorage.removeItem('X-TOKEN')
        navigate('/auth/login')
        return
      } else {
        throw new Error(data.returned_message || 'Error al actualizar el servicio')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }

  // Si no hay datos del servicio en el state, mostrar mensaje
  if (!servicio) {
    return (
      <>
        <PageTitle>Error</PageTitle>
        <div className='row g-5 g-xl-8'>
          <div className='col-xl-12'>
            <div className='card'>
              <div className='card-body'>
                <div className='alert alert-warning d-flex align-items-center p-5'>
                  <KTIcon iconName='information-5' className='fs-2hx text-warning me-4' />
                  <div className='d-flex flex-column'>
                    <h4 className='mb-1 text-dark'>No se encontraron datos del servicio</h4>
                    <span>Por favor, accede a esta página desde el listado de servicios.</span>
                  </div>
                  <button 
                    className='btn btn-light-primary ms-auto'
                    onClick={() => navigate('/servicios')}
                  >
                    Volver al listado
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <PageTitle>Editar Servicio</PageTitle>
      
      {/* Breadcrumb */}
      <div className='d-flex justify-content-between align-items-center mb-6'>
        <nav aria-label='breadcrumb'>
          <ol className='breadcrumb'>
            <li className='breadcrumb-item'>
              <button 
                className='btn btn-link p-0 text-decoration-none'
                onClick={() => navigate('/servicios')}
              >
                Servicios
              </button>
            </li>
            <li className='breadcrumb-item active' aria-current='page'>
              Editar
            </li>
          </ol>
        </nav>
        <button 
          className='btn btn-light-primary'
          onClick={() => navigate('/servicios')}
        >
          <KTIcon iconName='arrow-left' className='fs-3 me-1' />
          Volver
        </button>
      </div>

      <div className='row g-5 g-xl-8'>
        <div className='col-xl-12'>
          <div className='card'>
            <div className='card-body'>
              {error && (
                <div className='alert alert-danger' role='alert'>
                  {error}
                </div>
              )}
              
              <CreateServicioForm 
                onSubmit={handleSubmit} 
                loading={loading}
                initialData={servicio}
                isEditMode={true}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default EditServicioPage

